import React, { useState, useEffect } from 'react';
import {
  MegaphoneIcon,
  ArrowPathIcon,
  CloudArrowUpIcon,
  PhotoIcon,
  InformationCircleIcon,
  PaperAirplaneIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { api, API_BASE_URL, getAuthToken } from '../services/api';
import toast from '../utils/toast';
import SearchableSelect from '../components/SearchableSelect';

const CreateChannelPage = () => {
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [channelName, setChannelName] = useState('');
  const [channelDescription, setChannelDescription] = useState('');
  const [channelImage, setChannelImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    try {
      setLoading(true);
      const response = await api.getWhatsAppClients();
      setAgents(response.data.clients || []);
    } catch (error) {
      console.error('Error fetching WhatsApp clients:', error);
      toast.error('Failed to load WhatsApp clients');
    } finally {
      setLoading(false);
    }
  };

  // Prepare WhatsApp clients data for SearchableSelect
  const agentOptions = agents && Object.keys(agents).length > 0
    ? Object.values(agents)
        .filter(client => client.status === 'active')
        .map((client) => ({
          value: client.id,
          label: client.agent_name || client.display_name || `Client ${client.client_id}`,
          description: client.whatsapp_number || client.client_id,
          icon: '📱',
          port: client.port,
          webhookApiKey: client.webhook_api_key || client.api_key,
          clientId: client.client_id
        }))
    : [];

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        toast.error('Please select a valid image file (JPEG, PNG, WebP)');
        return;
      }

      // Validate file size (5MB max)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (file.size > maxSize) {
        toast.error('Image size must be less than 5MB');
        return;
      }

      setChannelImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
      
      toast.success('Channel image selected successfully!');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API_BASE_URL}/upload/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        },
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        return {
          url: result.url,
          mimetype: result.mimetype,
          filename: file.name
        };
      } else {
        throw new Error('Failed to upload image');
      }
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  };

  const handleCreateChannel = async () => {
    if (!selectedAgent || !channelName.trim() || !channelDescription.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setCreating(true);
    try {
      const client = Object.values(agents).find(a => a.id === selectedAgent);
      if (!client) {
        toast.error('WhatsApp client not found');
        return;
      }

      // Prepare channel data
      const channelData = {
        name: channelName.trim(),
        description: channelDescription.trim()
      };

      // Upload image if provided
      if (channelImage) {
        const imageData = await uploadImage(channelImage);
        channelData.picture = {
          mimetype: imageData.mimetype,
          filename: imageData.filename,
          url: imageData.url
        };
      }

      // Create the channel using the API service
      const response = await api.createChannel(client.client_id, channelData);

      toast.success('Channel created successfully!');
        
        // Reset form
        setChannelName('');
        setChannelDescription('');
        setChannelImage(null);
        setImagePreview(null);
        
        // Clear the file input
        const fileInput = document.getElementById('channel-image-input');
        if (fileInput) {
          fileInput.value = '';
        }
    } catch (error) {
      console.error('Error creating channel:', error);
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error(error.message || 'Failed to create channel');
      }
    } finally {
      setCreating(false);
    }
  };

  const clearImage = () => {
    setChannelImage(null);
    setImagePreview(null);
    const fileInput = document.getElementById('channel-image-input');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-whatsapp-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your agents...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <MegaphoneIcon className="h-8 w-8 text-purple-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-semibold text-gray-900">Create WhatsApp Channel</h1>
                  <p className="text-sm text-gray-600">Create a new broadcast channel for your WhatsApp Business</p>
                </div>
              </div>
              <button
                onClick={fetchAgents}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center">
              <MegaphoneIcon className="h-5 w-5 text-gray-400 mr-2" />
              <h2 className="text-lg font-medium text-gray-900">Channel Details</h2>
            </div>
          </div>

          <form onSubmit={(e) => { e.preventDefault(); handleCreateChannel(); }} className="px-6 py-6 space-y-6">

            {/* Agent Selection */}
            <div className="space-y-6">
              <h3 className="text-base font-medium text-gray-900 border-b border-gray-200 pb-2">
                Select WhatsApp Agent
              </h3>

              <SearchableSelect
                label="WhatsApp Agent"
                name="client"
                options={agentOptions}
                value={selectedAgent}
                onChange={(e) => setSelectedAgent(e.target.value)}
                placeholder="Search and select a WhatsApp agent..."
                isRequired={true}
                isSearchable={true}
                isClearable={true}
              />

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex">
                  <InformationCircleIcon className="h-5 w-5 text-blue-400" />
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-blue-800">WhatsApp Agent Selection</h4>
                    <p className="mt-1 text-sm text-blue-700">
                      Choose a connected WhatsApp Business agent to create the channel. Only agents with active connections are available.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Channel Details */}
            <div className="space-y-6">
              <h3 className="text-base font-medium text-gray-900 border-b border-gray-200 pb-2">
                Channel Information
              </h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Channel Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={channelName}
                  onChange={(e) => setChannelName(e.target.value)}
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500"
                  placeholder="Enter channel name"
                  required
                  maxLength="100"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Enter a descriptive name for your channel (max 100 characters)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Channel Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={channelDescription}
                  onChange={(e) => setChannelDescription(e.target.value)}
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500"
                  placeholder="Enter channel description"
                  required
                  rows="4"
                  maxLength="500"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Provide a brief description of what your channel is about (max 500 characters)
                </p>
              </div>

              {/* Channel Image Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Channel Image (Optional)
                </label>

                {/* Image Preview */}
                {imagePreview && (
                  <div className="mb-4 relative inline-block">
                    <img
                      src={imagePreview}
                      alt="Channel preview"
                      className="h-32 w-32 object-cover rounded-lg border border-gray-300"
                    />
                    <button
                      type="button"
                      onClick={clearImage}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                )}

                {/* File Upload Area - Only show if no image is selected */}
                {!channelImage && (
                  <div className="relative border-2 border-dashed border-gray-300 rounded-lg p-6 transition-colors hover:border-whatsapp-400 focus-within:border-whatsapp-500">
                    <div className="text-center">
                      <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="mt-4">
                        <label htmlFor="channel-image-input" className="cursor-pointer">
                          <span className="mt-2 block text-sm font-medium text-gray-900">
                            Upload a channel image
                          </span>
                          <span className="mt-1 block text-xs text-gray-500">
                            JPEG, PNG, WebP up to 5MB
                          </span>
                        </label>
                        <input
                          id="channel-image-input"
                          name="channel-image"
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/webp"
                          className="sr-only"
                          onChange={handleImageChange}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Selected Image Display */}
                {channelImage && (
                  <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center">
                      <PhotoIcon className="h-5 w-5 text-green-600 mr-3" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-green-800">{channelImage.name}</p>
                        <p className="text-xs text-green-600">{formatFileSize(channelImage.size)}</p>
                      </div>
                      <button
                        type="button"
                        onClick={clearImage}
                        className="text-green-600 hover:text-green-800"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}

                <div className="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-800 mb-2">Image Requirements:</h4>
                  <ul className="text-xs text-gray-600 space-y-1">
                    <li>• Recommended size: 512x512 pixels</li>
                    <li>• Supported formats: JPEG, PNG, WebP</li>
                    <li>• Maximum file size: 5MB</li>
                    <li>• Square images work best for channels</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  setSelectedAgent('');
                  setChannelName('');
                  setChannelDescription('');
                  clearImage();
                }}
                disabled={creating}
                className="px-6 py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Clear Form
              </button>

              <button
                type="submit"
                disabled={!selectedAgent || !channelName.trim() || !channelDescription.trim() || creating}
                className="px-8 py-3 text-sm font-medium text-white bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg hover:from-purple-600 hover:to-purple-700 transform hover:scale-[1.02] transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {creating ? (
                  <>
                    <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                    Creating Channel...
                  </>
                ) : (
                  <>
                    <MegaphoneIcon className="h-4 w-4 mr-2" />
                    Create Channel
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateChannelPage;